import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFeedback } from '@/components/feedback/feedback-context'
import { Select } from '@/components/forms/select'
import { Textarea } from '@/components/forms/textarea'
import { HelpTooltip } from '@/components/overlays/help-tooltip'
import { Modal } from '@/components/overlays/modal'
import { Button } from '@/components/primitives/button'
import { ApiError } from '@/lib/api/http-client'
import {
  saveWecomEmailPolicy,
  wecomIdentityProfilesQueryKey,
  type WecomEmailPolicy,
  type WecomIdentityProfile,
} from './wecom-identity-profiles-api'

const domainPattern =
  /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/
function parseDomains(value: string) {
  return [
    ...new Set(
      value
        .split(/[\s,，;；]+/)
        .map((part) => part.trim().toLowerCase())
        .filter(Boolean),
    ),
  ].sort()
}

/** 仅配置邮箱匹配规则；账号及邮箱由组织管理维护，不再单独登记。 */
export function WecomEmailBindingPanel({
  profile,
  onClose,
}: {
  profile: WecomIdentityProfile
  onClose: () => void
}) {
  const { t } = useTranslation('adminSettings')
  const domainsId = useId()
  const { showFeedback } = useFeedback()
  const queryClient = useQueryClient()
  const [saved, setSaved] = useState(profile)
  const [policy, setPolicy] = useState<WecomEmailPolicy>({
    mode: 'email_match',
    email_field: null,
    allowed_domains: [],
    trust_basis: null,
    ...profile.email_binding_policy,
  })
  const [domains, setDomains] = useState(policy.allowed_domains.join('\n'))
  const [validation, setValidation] = useState<string | null>(null)
  const parsedDomains = parseDomains(domains)
  const save = useMutation({
    mutationFn: () =>
      saveWecomEmailPolicy(
        profile.id,
        { ...policy, allowed_domains: parsedDomains, trust_basis: 'enterprise_directory' },
        saved.email_binding_policy_version ?? 1,
      ),
    onSuccess: async (result) => {
      setSaved(result)
      const updated = { ...policy, ...result.email_binding_policy }
      setPolicy(updated)
      setDomains(updated.allowed_domains.join('\n'))
      await queryClient.invalidateQueries({ queryKey: wecomIdentityProfilesQueryKey })
      showFeedback({ tone: 'success', message: t('wecomEmail.saved') })
    },
  })
  const savePolicy = () => {
    setValidation(null)
    if (
      parsedDomains.some((domain) => domain.length > 253 || !domainPattern.test(domain)) ||
      parsedDomains.length > 100
    ) {
      setValidation(t('wecomEmail.invalidDomains'))
      return
    }
    if (policy.mode === 'email_match' && (!policy.email_field || !parsedDomains.length)) {
      setValidation(t('wecomEmail.completePolicy'))
      return
    }
    save.mutate()
  }
  return (
    <Modal
      open
      onClose={() => !save.isPending && onClose()}
      closeLabel={t('wecomEmail.close')}
      title={t('wecomEmail.titleWithName', { name: profile.display_name })}
      className="!max-w-[680px] max-h-[calc(100dvh-48px)] grid-rows-[auto_minmax(0,1fr)_auto]"
      bodyClassName="min-h-0 overflow-y-auto"
      footer={
        <>
          <Button variant="secondary" disabled={save.isPending} onClick={onClose}>
            {t('wecomEmail.cancel')}
          </Button>
          <Button isLoading={save.isPending} onClick={savePolicy}>
            {t('wecomEmail.savePolicy')}
          </Button>
        </>
      }
    >
      <div className="grid gap-5">
        <p className="rounded-lg bg-[var(--color-surface-muted)] p-4 text-sm leading-6 text-[var(--color-text-muted)]">
          {t('wecomEmail.governance')}
        </p>
        {validation || save.error ? (
          <p role="alert" className="text-sm text-[var(--color-danger)]">
            {validation ??
              (save.error instanceof ApiError && save.error.status === 409
                ? t('wecomEmail.conflict')
                : t('wecomEmail.failed'))}
          </p>
        ) : null}
        <label className="grid gap-2 text-sm">
          {t('wecomEmail.mode')}
          <Select
            aria-label={t('wecomEmail.mode')}
            disabled={save.isPending}
            value={policy.mode}
            onValueChange={(value) => {
              setPolicy({ ...policy, mode: value as WecomEmailPolicy['mode'] })
              setValidation(null)
            }}
            options={[
              { value: 'manual', label: t('wecomEmail.manual') },
              { value: 'email_match', label: t('wecomEmail.emailMatch') },
            ]}
          />
        </label>
        <label className="grid gap-2 text-sm">
          {t('wecomEmail.field')}
          <Select
            aria-label={t('wecomEmail.field')}
            disabled={save.isPending}
            value={policy.email_field ?? ''}
            onValueChange={(value) => {
              setPolicy({ ...policy, email_field: value as 'email' | 'biz_mail' })
              setValidation(null)
            }}
            options={[
              { value: '', label: t('wecomEmail.selectField') },
              { value: 'email', label: t('wecomEmail.contactEmail') },
              { value: 'biz_mail', label: t('wecomEmail.businessEmail') },
            ]}
          />
        </label>
        <div className="grid gap-2 text-sm">
          <div className="flex items-center gap-2">
            <label htmlFor={domainsId}>{t('wecomEmail.domains')}</label>
            <HelpTooltip label={t('wecomEmail.domainsHelp')} widthClassName="w-[280px]">
              {t('wecomEmail.domainsHelp')}
            </HelpTooltip>
          </div>
          <Textarea
            id={domainsId}
            disabled={save.isPending}
            placeholder={t('wecomEmail.domainsPlaceholder')}
            value={domains}
            onChange={(event) => {
              setDomains(event.target.value)
              setValidation(null)
            }}
          />
        </div>
      </div>
    </Modal>
  )
}
